import React from 'react';
import * as LucideIcons from 'lucide-react';
import { LucideProps } from 'lucide-react';
import clsx from 'clsx';

export type IconSize =
  | 'xxs'
  | 'xs'
  | 's'
  | 'm'
  | 'l'
  | 'xl'
  | 'xxl'
  | 'xxxl'
  | number;

interface LucideIconProps extends Omit<LucideProps, 'size' | 'name'> {
  name?: keyof typeof LucideIcons | React.ElementType | string;
  size?: IconSize | number | string;
  color?: string;
}

const sizeMap: Record<IconSize, string> = {
  xxs: 'calc(var(--unit-minthr) * 9)',
  xs: 'calc(var(--unit-minthr) * 13)',
  s: 'calc(var(--unit-minthr) * 15)',
  m: 'calc(var(--unit-minthr) * 19)',
  l: 'calc(var(--unit-minthr) * 23)',
  xl: 'calc(var(--unit-minthr) * 37)',
  xxl: 'calc(var(--unit-minthr) * 53)',
  xxxl: 'calc(var(--unit-minthr) * 86)',
};

/**
 * Global Responsive Lucide Icon component.
 * Uses CSS variables to ensure icons scale correctly across all devices including large monitors.
 */
const LucideIcon: React.FC<LucideIconProps> = ({
  name,
  size = 'm',
  className,
  style,
  color,
  ...props
}) => {
  if (!name) return null;

  const IconComponent =
    typeof name === 'string'
      ? (LucideIcons[name as keyof typeof LucideIcons] as React.ElementType)
      : name;

  if (!IconComponent) {
    console.warn(`Icon "${name}" not found in lucide-react`);
    return null;
  }

  const resolvedSize =
    typeof size === 'string' && size in sizeMap
      ? sizeMap[size as IconSize]
      : `calc(var(--unit-minthr) * ${size})`;

  return (
    <IconComponent
      size={resolvedSize}
      className={clsx('tw-flex-shrink-0', className)}
      color={color}
      style={{
        width: resolvedSize,
        height: resolvedSize,
        minWidth: resolvedSize,
        ...style,
      }}
      {...props}
    />
  );
};

export default LucideIcon;
